// ReportMetricPie.jsx

import React, { useMemo } from "react";
import { Box, Container, Table, TableBody, Typography } from "@mui/material";
import EChartsReact from "echarts-for-react";

import {
    DISPLAY_ELEMENT_HELPER,
    GROUP_COUNTRIES,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_WEEK,
    GROUP_DATE_YEAR,
    COMPARISON_FILTER,
} from "../../../helpers/filters";
import { getWeekYear } from "../../../helpers/dates";
import HoverPaper from "../HoverPaper";
import NoDataRow from "../../NoDataRow";
import Colours from "../../../helpers/colours";
import InfoIcon from "../../InfoIcon";

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const REPORT_METRIC_PIE = "REPORT_METRIC_PIE";
const ReportMetricPie = ({
    data,
    title,
    description,
    metric = "revenueTotal",
    aggregation = "Country",
    hole = "45%",
    showLegend = true,
    entriesLimit = 6, // 5 elements displayed + "Others"
    comparisonText = "",
}) => {
    const radius = useMemo(() => {
        if (!hole) return "70%";

        const innerRadius =
            hole === true
                ? "45%"
                : typeof hole === "number"
                  ? `${hole <= 1 ? hole * 100 : hole}%`
                  : hole;

        return [innerRadius, "70%"];
    }, [hole]);

    const chartData = useMemo(() => {
        const grouped = {};

        Object.values(data ?? {}).forEach(
            ({ keys, result, resultOneYearBefore }) => {
                const k = Object.fromEntries(keys ?? []);

                const country = String(k[GROUP_COUNTRIES] ?? "Unknown");

                const year = Number(
                    k[GROUP_DATE_YEAR] ?? new Date().getUTCFullYear(),
                );
                const month = Number(k[GROUP_DATE_MONTH] ?? 1);
                const day = Number(k[GROUP_DATE_DAY] ?? 1);
                const date = new Date(Date.UTC(year, month - 1, day));
                const week = Number(
                    k[GROUP_DATE_WEEK] ?? getWeekYear(date).week,
                );
                const weekdayIndex = (date.getUTCDay() + 6) % 7;

                const key =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
                          : aggregation === "Week"
                            ? `${year} - Week #${week}`
                            : aggregation === "Weekday"
                              ? WEEKDAYS[weekdayIndex]
                              : aggregation === "Year"
                                ? `${year}`
                                : `${year}-${String(month).padStart(2, "0")}`;

                const label =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? `${day} ${MONTHS[month - 1]} ${year}`
                          : aggregation === "Week"
                            ? `Week #${week} - ${year}`
                            : aggregation === "Weekday"
                              ? WEEKDAYS[weekdayIndex]
                              : aggregation === "Year"
                                ? `${year}`
                                : `${MONTHS[month - 1]} ${year}`;

                const sort =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? year * 10000 + month * 100 + day
                          : aggregation === "Week"
                            ? year * 100 + week
                            : aggregation === "Weekday"
                              ? weekdayIndex
                              : aggregation === "Year"
                                ? year
                                : year * 100 + month;

                grouped[key] = grouped[key] ?? {
                    name: label,
                    value: 0,
                    comparisonValue: 0,
                    sort,
                };

                grouped[key].value += Number(result?.[metric]) || 0;
                grouped[key].comparisonValue +=
                    Number(resultOneYearBefore?.[metric]) || 0;
            },
        );

        const sortRows = (a, b) =>
            typeof a.sort === "string"
                ? a.sort.localeCompare(b.sort)
                : a.sort - b.sort;

        const rows = Object.values(grouped)
            .filter((row) => row.value > 0)
            .sort(sortRows);

        const limit = Number(entriesLimit) || 0;

        if (limit <= 0 || rows.length <= limit) return rows;

        const sortedByValue = [...rows].sort((a, b) => b.value - a.value);
        const visibleRows = sortedByValue.slice(0, Math.max(limit - 1, 0));
        const othersRows = sortedByValue.slice(Math.max(limit - 1, 0));

        const othersValue = othersRows.reduce(
            (total, row) => total + row.value,
            0,
        );

        const othersComparisonValue = othersRows.reduce(
            (total, row) => total + row.comparisonValue,
            0,
        );

        return [
            ...visibleRows.sort(sortRows),
            {
                name: "Others",
                value: othersValue,
                comparisonValue: othersComparisonValue,
                sort: Number.MAX_SAFE_INTEGER,
            },
        ];
    }, [data, metric, aggregation, entriesLimit]);

    const metricName = DISPLAY_ELEMENT_HELPER[metric]?.short ?? metric;
    const comparisonName =
        COMPARISON_FILTER[comparisonText]?.label ?? comparisonText;

    const formatValue = (value) => {
        const n = Number(value) || 0;
        const format = DISPLAY_ELEMENT_HELPER[metric]?.format ?? {};
        const maxDecimals = format.maxDecimals ?? (Number.isInteger(n) ? 0 : 2);

        const formatted = n.toLocaleString(undefined, {
            maximumFractionDigits: maxDecimals,
        });

        if (format.left === "currency") return `€${formatted}`;
        if (format.right === "currency") return `${formatted} €`;
        if (format.right) return `${formatted}${format.right}`;

        return formatted;
    };

    return (
        <>
            <InfoIcon title={title} description={description} />

            <Box
                component={HoverPaper}
                sx={{
                    p: 2,
                    width: "100%",
                    minWidth: 0,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {" "}
                {chartData.length > 0 ? (
                    <EChartsReact
                        style={{
                            width: "100%",
                            height: 320,
                        }}
                        option={{
                            tooltip: {
                                trigger: "item",
                                formatter: ({
                                    marker,
                                    name,
                                    value,
                                    color,
                                    data,
                                }) => {
                                    const comparisonValue =
                                        Number(data?.comparisonValue) || 0;
                                    const diff =
                                        (Number(value) || 0) - comparisonValue;
                                    const diffPercent = comparisonValue
                                        ? ` (${diff > 0 ? "+" : ""}${(
                                              (diff / comparisonValue) *
                                              100
                                          ).toLocaleString(undefined, {
                                              maximumFractionDigits: 2,
                                          })}%)`
                                        : "";

                                    const softMarker = `<span style="
                                                        display:inline-block;
                                                        width:10px;
                                                        height:10px;
                                                        border-radius:50%;
                                                        background:${color};
                                                        opacity:.35;
                                                        margin-right:6px;
                                                    "></span>`;

                                    const differenceBackgroundColour =
                                        diff >= 0
                                            ? Colours.trafficLightGreen
                                            : Colours.trafficLightRed;

                                    const differenceMarker = `<span style="
                                                            display:inline-block;
                                                            width:10px;
                                                            height:10px;
                                                            border-radius:50%;
                                                            background:${differenceBackgroundColour};
                                                            border:1px solid ${differenceBackgroundColour};
                                                            opacity:.75;
                                                            margin-right:6px;
                                                        "></span>`;

                                    return `
                                <div style="min-width:190px">
                                    <div style="display:flex; justify-content:space-between; gap:18px;">
                                        <span>${marker}${name}</span>
                                        <b>${formatValue(value)}</b>
                                    </div>
                                    ${
                                        comparisonText
                                            ? `
                                                <div style="display:flex; justify-content:space-between; gap:18px;">
                                                    <span>${softMarker}${comparisonName}</span>
                                                    <b>${formatValue(comparisonValue)}</b>
                                                </div>
                                                <div style="display:flex; justify-content:space-between; gap:18px;">
                                                    <span>${differenceMarker}Difference</span>
                                                    <b>${diff > 0 ? "+" : ""}${formatValue(diff)}${diffPercent}</b>
                                                </div>
                    `
                                            : ""
                                    }
        </div>
    `;
                                },
                            },
                            legend: {
                                show: showLegend,
                                bottom: 0,
                                left: "center",
                            },
                            series: [
                                {
                                    type: "pie",
                                    name: metricName,
                                    radius,
                                    center: ["50%", "40%"],
                                    data: chartData,
                                    label: {
                                        formatter: "{d}%",
                                    },
                                    emphasis: {
                                        itemStyle: {
                                            shadowBlur: 10,
                                            shadowOffsetX: 0,
                                        },
                                    },
                                },
                            ],
                        }}
                    />
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No data for these filters" />
                        </TableBody>
                    </Table>
                )}
            </Box>
        </>
    );
};

export default ReportMetricPie;
